import React from "react";

const SIBLING_COUNT = 1;
const DOTS = "...";

function getPageRange(start, end) {
  const length = end - start + 1;
  return Array.from({ length }, (_, i) => start + i);
}

function buildPageNumbers(currentPage, totalPages) {
  const totalPageNumbers = SIBLING_COUNT * 2 + 5; // siblings + first + last + 2 dots + current

  if (totalPages <= totalPageNumbers) {
    return getPageRange(1, totalPages);
  }

  const leftSibling = Math.max(currentPage - SIBLING_COUNT, 1);
  const rightSibling = Math.min(currentPage + SIBLING_COUNT, totalPages);

  const showLeftDots = leftSibling > 2;
  const showRightDots = rightSibling < totalPages - 1;

  if (!showLeftDots && showRightDots) {
    const leftRange = getPageRange(1, 3 + 2 * SIBLING_COUNT);
    return [...leftRange, DOTS, totalPages];
  }

  if (showLeftDots && !showRightDots) {
    const rightRange = getPageRange(totalPages - (3 + 2 * SIBLING_COUNT) + 1, totalPages);
    return [1, DOTS, ...rightRange];
  }

  const middleRange = getPageRange(leftSibling, rightSibling);
  return [1, DOTS, ...middleRange, DOTS, totalPages];
}

/**
 * Reusable Pagination Component
 *
 * Props:
 *   currentPage   {number}   - current active page (1-indexed)
 *   totalItems    {number}   - total number of records returned by the API
 *   itemsPerPage  {number}   - records per page (limit)
 *   onPageChange  {function} - callback(newPage) fired when user clicks a page
 *   loading       {boolean}  - optional: disables buttons while fetching
 */
function Pagination({ currentPage, totalItems, itemsPerPage, onPageChange, loading = false }) {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (totalPages <= 1) return null;

  const pages = buildPageNumbers(currentPage, totalPages);

  const handleClick = (page) => {
    if (page === DOTS || page === currentPage || loading) return;
    onPageChange(page);
  };

  return (
    <nav aria-label="Order pagination" className="d-flex justify-content-center align-items-center mt-4 flex-wrap gap-1">
      {/* Prev button */}
      <button
        className="btn btn-sm btn-outline-secondary"
        style={{ minWidth: "80px" }}
        onClick={() => handleClick(currentPage - 1)}
        disabled={currentPage === 1 || loading}
      >
        &laquo; Prev
      </button>

      {/* Page numbers */}
      {pages.map((page, idx) =>
        page === DOTS ? (
          <span key={`dots-${idx}`} className="px-2 text-secondary align-self-center">
            &hellip;
          </span>
        ) : (
          <button
            key={page}
            onClick={() => handleClick(page)}
            disabled={loading}
            className="btn btn-sm"
            style={{
              minWidth: "38px",
              fontWeight: currentPage === page ? "700" : "400",
              backgroundColor: currentPage === page ? "#ff6b35" : "transparent",
              color: currentPage === page ? "#fff" : "#495057",
              border: `1px solid ${currentPage === page ? "#ff6b35" : "#dee2e6"}`,
            }}
          >
            {page}
          </button>
        )
      )}

      {/* Next button */}
      <button
        className="btn btn-sm btn-outline-secondary"
        style={{ minWidth: "80px" }}
        onClick={() => handleClick(currentPage + 1)}
        disabled={currentPage === totalPages || loading}
      >
        Next &raquo;
      </button>

      {/* Info text */}
      <small className="text-muted w-100 text-center mt-1">
        Page {currentPage} of {totalPages} &nbsp;&mdash;&nbsp; {totalItems} total record{totalItems !== 1 ? "s" : ""}
      </small>
    </nav>
  );
}

export default Pagination;
